import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Paper } from '@mui/material';
import { useMessageQueue } from '@erickson/components';
import { UserContext } from '@/context/userContext';
import { MLInactiveFlagsUtils } from '@/utils/CountMapUtils';
import { FilterValue, FlagDefinition } from '@/API';
import { FlagDefinitionService } from '@/service/FlagDefinitionService';
import { useMLInactiveFlags } from './hooks/useMLInactiveFlags';
import SummaryAndControls from './components/SummaryAndControls';
import FlagsList from './components/FlagsList';
import MLInactiveFlagsVersionHistory from './VersionHistory';
import log from 'loglevel';

const MLInactiveFlagsManager: React.FC = () => {
  const messageQueue = useRef(useMessageQueue()).current;
  const { user } = useContext(UserContext);
  const [flagService] = useState(() => new FlagDefinitionService());
  const [flagDefinitions, setFlagDefinitions] = useState<FlagDefinition[]>([]);

  const {
    flags,
    loading,
    updating,
    saving,
    hasChanges,
    mlInactiveFlagsFilter,
    totalRemainingRecords,
    enabledCount,
    totalCount,
    actions,
  } = useMLInactiveFlags();

  // Load flag definitions for ML Inactive flags
  useEffect(() => {
    const loadFlagDefinitions = async () => {
      if (mlInactiveFlagsFilter?.databaseMetadata?.columnName) {
        try {
          const definitions = await flagService.getFlagDefinitionsByField(
            'houselist', // ML Inactive flags are typically from house list
            mlInactiveFlagsFilter.databaseMetadata.columnName
          );
          setFlagDefinitions(definitions);
        } catch (error) {
          log.warn(
            'Failed to load flag definitions for ML Inactive flags:',
            error
          );
          // Don't show error to user as this is just for enhanced display
        }
      }
    };

    loadFlagDefinitions();
  }, [mlInactiveFlagsFilter?.databaseMetadata?.columnName, flagService]);

  const handleAction = async (
    action: () => Promise<void>,
    successMessage?: string
  ) => {
    try {
      await action();
      if (successMessage) {
        messageQueue.addMessage({ text: successMessage, severity: 'success' });
      }
    } catch {
      messageQueue.addMessage({
        text: 'An error occurred. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleSave = () =>
    handleAction(async () => {
      await actions.saveChanges(user?.email);
    }, 'ML Inactive Flags default values saved successfully');

  const handleRestoreVersion = (version: FilterValue) =>
    handleAction(
      () => actions.restoreVersion(version),
      `Restored to version ${version.version}`
    );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!mlInactiveFlagsFilter) {
    return (
      <Alert severity="error">
        ML Inactive Flags filter definition not found. Please check the
        configuration.
      </Alert>
    );
  }

  const displayFlags = MLInactiveFlagsUtils.filterOutCountFlags(flags);

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <SummaryAndControls
          enabledCount={enabledCount}
          totalCount={totalCount}
          totalRemainingRecords={totalRemainingRecords}
          hasChanges={hasChanges}
          saving={saving}
          updating={updating}
          onSelectAll={() => handleAction(actions.selectAll)}
          onClearAll={() => handleAction(actions.clearAll)}
          onSave={handleSave}
        />
      </Paper>

      <Box sx={{ mb: 3 }}>
        <MLInactiveFlagsVersionHistory
          onRestoreVersion={handleRestoreVersion}
          disabled={saving || updating}
        />
      </Box>

      <Paper elevation={1}>
        {displayFlags.length > 0 ? (
          <FlagsList
            flags={displayFlags}
            flagDefinitions={flagDefinitions}
            onToggle={flagKey =>
              handleAction(() => actions.toggleFlag(flagKey))
            }
            disabled={saving || updating}
            updating={updating}
          />
        ) : (
          <Box p={3}>
            <Alert severity="warning">
              No ML Inactive Flags found. This might indicate a data or
              configuration issue.
            </Alert>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MLInactiveFlagsManager;
